import {
	IconButton,
	SearchInput,
	SearchSelect,
	SearchSelectOptionProps,
	Select,
} from "@alm/flow-ds/components";
import PageHeader from "components/PageHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";

interface Props {
	allManagerRoles: string[];
	allManagerUsers: string[];
	status: string;
	candidate?: SearchSelectOptionProps;
	assignee: string;
	onChangeSearchValue: (value: string) => void;
	onChangeStatusValue: (value: string) => void;
	onChangeCandidateValue: (value: SearchSelectOptionProps) => void;
	onChangeAssigneeValue: (value: string) => void;
}

const TasksManagerViewHeader = ({
	allManagerRoles,
	allManagerUsers,
	status,
	candidate,
	assignee,
	onChangeSearchValue,
	onChangeStatusValue,
	onChangeCandidateValue,
	onChangeAssigneeValue,
}: Props) => {
	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();

	const ALL = "ALL";
	const DESKTOP_WIDTH = 1100;

	const [width, setWidth] = useState(window.innerWidth);
	const handleWindowSizeChange = () => setWidth(window.innerWidth);

	const [mobileOpen, setMobileOpen] = useState(false);

	const [searchBox, setSearchBox] = useState("");

	const [allOptions, setAllOptions] = useState<
		SearchSelectOptionProps[] | undefined
	>(undefined);
	const [roleOptions, setRoleOptions] = useState<
		SearchSelectOptionProps[] | undefined
	>(undefined);
	const [userOptions, setUserOptions] = useState<
		SearchSelectOptionProps[] | undefined
	>(undefined);

	const handleSearchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Search input valued changed to "${value}"`,
			});
			if (value !== "") {
				setSearchBox(value);
			} else {
				setSearchBox(value);
				onChangeSearchValue(value);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const handleSelectStatusChange = useCallback(
		(
			e: React.ChangeEvent<HTMLSelectElement>,
			type: "status" | "assignee"
		) => {
			const value = e.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Select value changed to "${value}"`,
			});

			const params = new URLSearchParams(location.search);

			params.set(type, value.toLowerCase());

			history.push({
				pathname: location.pathname,
				search: params.toString(),
			});

			if (type === "status") onChangeStatusValue(value);
			else if (type === "assignee") onChangeAssigneeValue(value);
		},
		[
			history,
			location.pathname,
			location.search,
			onChangeStatusValue,
			onChangeAssigneeValue,
		]
	);

	const candidateOptions = useMemo(() => {
		return [
			{
				label: t("main.tasks.labels.candidateAll"),
				options: allOptions || [],
			},
			{
				label: t("main.tasks.labels.candidateRoles"),
				options: roleOptions || [],
			},
			{
				label: t("main.tasks.labels.candidateUsers"),
				options: userOptions || [],
			},
		];
	}, [allOptions, roleOptions, t, userOptions]);

	useEffect(() => {
		const roleOptions = allManagerRoles.map(role => {
			return { label: role, value: role };
		});
		const userOptions = allManagerUsers.map(user => {
			return { label: user, value: user };
		});

		setRoleOptions(roleOptions);
		setUserOptions(userOptions);
		setAllOptions([
			{
				value: "ALL_ROLES",
				label: t("main.tasks.headings.allRoles"),
			},
			{
				value: "ALL_USERS",
				label: t("main.tasks.headings.allUsers"),
			},
		]);
	}, [allManagerRoles, allManagerUsers, t]);

	useEffect(() => {
		window.addEventListener("resize", handleWindowSizeChange);
		return () => {
			window.removeEventListener("resize", handleWindowSizeChange);
		};
	}, []);

	const handleCandidateChange = useCallback(
		newOption => {
			const newVal = newOption.value;
			onChangeCandidateValue(newOption);

			addSentryBreadcrumb({
				category: "input.change",
				message: `Select value changed to "${newVal}"`,
			});

			const params = new URLSearchParams(location.search);

			let param = "";

			if (newVal === "ALL_ROLES" || allManagerRoles.includes(newVal)) {
				param = "candidate_role";
			}

			if (newVal === "ALL_USERS" || allManagerUsers.includes(newVal)) {
				param = "candidate_user";
			}

			if (param) {
				params.delete("candidate_role");
				params.delete("candidate_user");
				params.set(param, newVal.toLowerCase());
			}

			history.push({
				pathname: location.pathname,
				search: params.toString(),
			});
		},
		[
			allManagerRoles,
			allManagerUsers,
			history,
			location.pathname,
			location.search,
			onChangeCandidateValue,
		]
	);

	const getStatusAndCandidateSelect = (
		<>
			<Select
				className="tasks__select--managers"
				value={status}
				onChange={e => handleSelectStatusChange(e, "status")}
				label={t("main.tasks.labels.taskStatus")}>
				<>
					<option value="ALL">
						{t("main.tasks.headings.allTasks")}
					</option>
					<option value="ACTIVE">
						{t("main.tasks.headings.active")}
					</option>
					<option value="COMPLETED">
						{t("main.tasks.headings.completed")}
					</option>
				</>
			</Select>
			<SearchSelect
				className="tasks__search-select--managers"
				value={candidate}
				options={candidateOptions}
				onChange={handleCandidateChange}
				label={t("main.tasks.labels.candidates")}
			/>
		</>
	);

	const getAssigneeAndSearchBar = (
		<>
			<Select
				className="tasks__select--managers"
				value={assignee}
				onChange={e => handleSelectStatusChange(e, "assignee")}
				label={t("main.tasks.labels.assignee")}>
				<>
					<option value={ALL}>
						{t("main.tasks.headings.allAssignees")}
					</option>
					<option value="assigned">
						{t("main.tasks.headings.assigned")}
					</option>
					<option value="unassigned">
						{t("main.tasks.headings.unassigned")}
					</option>
					{allManagerUsers.map((assignee, index) => (
						<option value={assignee} key={`${assignee}-${index}`}>
							{assignee}
						</option>
					))}
				</>
			</Select>

			<div className="elem search-bar search-bar--desktop search-bar--managers">
				<SearchInput
					label={t("main.text.search")}
					onSearch={() => onChangeSearchValue(searchBox)}
					onChange={handleSearchChange}
					value={searchBox}
				/>
			</div>
		</>
	);

	return (
		<div className="tasks__header-container--managers">
			<PageHeader
				title={t("main.tasks.headings.managersView")}
				className="page-header__tasks">
				<div
					className="search-bar search-bar--icon"
					aria-expanded={mobileOpen}>
					<IconButton
						aria-label={t("main.icons.openSearchBox")}
						active={mobileOpen}
						className="btn-basic--primary btn--round"
						icon="search"
						onClick={() => setMobileOpen(!mobileOpen)}
					/>
				</div>
			</PageHeader>
			<div className="nav tasks__nav">
				{mobileOpen && (
					<div className="search-bar--mobile">
						<SearchInput
							label={t("main.text.search")}
							onSearch={() => onChangeSearchValue(searchBox)}
							onChange={handleSearchChange}
							value={searchBox}
						/>
					</div>
				)}

				{width <= DESKTOP_WIDTH ? (
					<>
						<div className="tasks__nav-wrapper">
							{getStatusAndCandidateSelect}
						</div>
						<div className="tasks__nav-wrapper">
							{getAssigneeAndSearchBar}
						</div>
					</>
				) : (
					<>
						{getStatusAndCandidateSelect}
						{getAssigneeAndSearchBar}
					</>
				)}
			</div>
		</div>
	);
};

export default TasksManagerViewHeader;
